import { Text } from '@react-three/drei';
import { type ThreeEvent, useFrame } from '@react-three/fiber';
import { useMemo, useRef, useState } from 'react';
import {
  AdditiveBlending,
  Color,
  type Group,
  MathUtils,
  type Mesh,
  type ShaderMaterial,
} from 'three';
import { useNavigationStore } from '@/store/useNavigationStore';
import { palette } from '@/theme/palette';
import type { PageMeta } from './pages';

/**
 * One spoke of the kavşak rendered as a "star road" — a long flat
 * ribbon laid on the platform with scrolling light stripes flowing
 * outward, and a roadside signpost (pole + plate) on the right
 * shoulder at the start of the road. Clicking the signpost begins
 * a warp toward this page.
 *
 * Authored in highway-local space (forward = -Z, sign on +X side,
 * destination frame at z = -92). The parent group rotates this whole
 * spoke by `meta.yaw` so it points in the right world direction.
 */

const ROAD_LENGTH = 86;
const ROAD_WIDTH = 2.4;

const ROAD_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const ROAD_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform float uActive;
  uniform vec3 uColor;
  varying vec2 vUv;

  void main() {
    // vUv.x ∈ 0..1 across width, vUv.y along length.
    float dx = abs(vUv.x - 0.5);

    // Two thin shoulder lines (left + right edge of the road)
    float shoulder = smoothstep(0.5, 0.42, dx) - smoothstep(0.42, 0.34, dx);

    // Faint centre stripe so the road reads as a corridor
    float center = smoothstep(0.04, 0.0, dx);

    // Length fade — fade in from the junction, fade out toward the far end
    float lengthFade = smoothstep(0.0, 0.06, vUv.y) * (1.0 - smoothstep(0.92, 1.0, vUv.y));

    // Flowing star-points: bright dots that slide along the centreline
    float flow = fract(vUv.y * 4.0 - uTime * (0.25 + uActive * 2.4));
    float dot = smoothstep(0.92, 1.0, flow);
    float starpoint = dot * smoothstep(0.06, 0.0, dx);

    float intensity = (shoulder * 0.35 + center * 0.30 + starpoint * 1.6) * lengthFade;
    intensity *= 0.55 + uActive * 0.7;

    if (intensity < 0.005) discard;
    gl_FragColor = vec4(uColor, intensity);
  }
`;

interface HighwayProps {
  meta: PageMeta;
}

export function Highway({ meta }: HighwayProps) {
  const navigateTo = useNavigationStore((s) => s.navigateTo);
  const phase = useNavigationStore((s) => s.phase);
  const activePage = useNavigationStore((s) => s.page);
  const isActive = activePage === meta.id;
  const isIdle = phase === 'idle';

  const [hovered, setHovered] = useState(false);

  const roadMatRef = useRef<ShaderMaterial>(null);
  const plateRef = useRef<Mesh>(null);
  const plateGroupRef = useRef<Group>(null);
  const screenRef = useRef<Mesh>(null);

  const roadUniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uActive: { value: 0 },
      uColor: { value: new Color(meta.color) },
    }),
    [meta.color],
  );

  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    roadUniforms.uTime.value = t;
    const target = isActive && !isIdle ? 1.0 : 0.0;
    const lerp = 1 - Math.exp(-dt * 4);
    roadUniforms.uActive.value += (target - roadUniforms.uActive.value) * lerp;

    // Plate orientation tracks camera height: when the camera is high
    // (bird's-eye idle) the plate lays flat with its label facing UP, so
    // it can be read from above; when the camera descends to road level
    // the plate stands up vertical so it reads like a real signpost.
    // The plate stays aligned with its highway (no counter-rotation) so
    // the signposts read as roadside markers along each spoke.
    if (plateGroupRef.current) {
      const camY = state.camera.position.y;
      const orient = MathUtils.clamp((camY - 3) / 9, 0, 1);
      plateGroupRef.current.rotation.x = -orient * (Math.PI / 2);
      plateGroupRef.current.position.y = MathUtils.lerp(1.65, 2.15, orient);
    }

    // Hover scale lift on the plate itself
    if (plateRef.current) {
      const liftTarget = hovered && isIdle ? 1.06 : 1.0;
      plateRef.current.scale.x += (liftTarget - plateRef.current.scale.x) * lerp;
      plateRef.current.scale.y += (liftTarget - plateRef.current.scale.y) * lerp;
    }

    // Destination frame: subtle pulse when arrived at this page
    if (screenRef.current) {
      const targetScale = isActive && phase === 'arrived' ? 1.04 : 1.0;
      screenRef.current.scale.x +=
        (targetScale - screenRef.current.scale.x) * lerp;
      screenRef.current.scale.y +=
        (targetScale - screenRef.current.scale.y) * lerp;
    }

  });

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    if (!isIdle) return;
    navigateTo(meta.id);
  };

  return (
    <group rotation={[0, meta.yaw, 0]}>
      {/* Star-road ribbon laid along -Z, just above the platform */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.015, -ROAD_LENGTH / 2 - 1.8]}
      >
        <planeGeometry args={[ROAD_WIDTH, ROAD_LENGTH]} />
        <shaderMaterial
          ref={roadMatRef}
          vertexShader={ROAD_VERTEX}
          fragmentShader={ROAD_FRAGMENT}
          uniforms={roadUniforms}
          transparent
          blending={AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Roadside signpost — pole + a single plate that pivots between
          horizontal (label-up, readable from bird's-eye idle) and
          vertical (label facing +Z, readable from ground level / VR).
          The plateGroup's rotation/Y are driven by the camera height in
          useFrame so the plate "lies down" as the camera rises. */}
      <group position={[1.65, 0, -3.4]}>
        {/* Pole — tall enough to host the plate at either orientation */}
        <mesh position={[0, 1.05, 0]}>
          <cylinderGeometry args={[0.05, 0.05, 2.1, 10]} />
          <meshStandardMaterial color="#1c2c5c" metalness={0.7} roughness={0.4} />
        </mesh>

        {/* Pivoting plate group (positioned + rotated each frame) */}
        <group ref={plateGroupRef} position={[0, 1.65, 0]}>
          <mesh
            ref={plateRef}
            position={[0, 0, 0.07]}
            onClick={handleClick}
            onPointerOver={() => setHovered(true)}
            onPointerOut={() => setHovered(false)}
          >
            <boxGeometry args={[2.5, 0.9, 0.06]} />
            <meshStandardMaterial
              color="#0d1b4a"
              emissive={meta.color}
              emissiveIntensity={hovered ? 0.55 : 0.2}
              metalness={0.5}
              roughness={0.45}
            />
          </mesh>

          {/* Label sits in the plate's local frame so it reads along
              the plate's long axis — text "tilt" matches the rectangle
              direction in screen space (the user's preferred look). */}
          <Text
            position={[0, 0, 0.11]}
            fontSize={0.22}
            color={palette.text}
            anchorX="center"
            anchorY="middle"
            maxWidth={2.3}
            textAlign="center"
            lineHeight={1.05}
            letterSpacing={-0.005}
          >
            {meta.label}
          </Text>

          {/* Accent stripe along the top edge of the plate (in the plate's
              local frame so it follows the rotation). */}
          <mesh position={[0, 0.41, 0.105]}>
            <boxGeometry args={[2.4, 0.04, 0.005]} />
            <meshStandardMaterial
              color={meta.color}
              emissive={meta.color}
              emissiveIntensity={1.8}
              toneMapped={false}
            />
          </mesh>
        </group>
      </group>

      {/* Far-end destination frame (subtle in idle, scales when arrived) */}
      <group position={[0, 1.6, -92]}>
        <mesh ref={screenRef}>
          <planeGeometry args={[10.5, 6.1]} />
          <meshBasicMaterial color={meta.color} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.01]}>
          <planeGeometry args={[10.0, 5.65]} />
          <meshBasicMaterial color="#040a30" />
        </mesh>
      </group>
    </group>
  );
}
